import {useEffect, useState} from "react";
import {getTrainers} from "../../services/TrainerService";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogContent,
    TextField,
    Typography,
    IconButton,
    Avatar
} from "@mui/material";
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useMessageContext} from "../../contexts/MessageContext";
import {colors, borders} from "../ui/colors";

const ChangeTrainerDialog = ({companyId, trainer, isOpen, close, handleSubmit, title}) => {

    const [currentTrainer, setCurrentTrainer] = useState(trainer ? {...trainer} : null);
    const [trainers, setTrainers] = useState([]);

    const {notificationHandler} = useMessageContext();

    useEffect(() => {
        if (isOpen) {
            const fetchTrainers = getTrainers(companyId);

            fetchTrainers
                .then(res => setTrainers(res.data))
                .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));

            setCurrentTrainer(trainer ? {...trainer} : null);
        }
    }, [isOpen, trainer]);

    return (
        <Dialog
            open={isOpen}
            onClose={close}
            PaperProps={{
                sx: {
                    borderRadius: borders.radius.lg,
                    minWidth: {xs: "90%", sm: "420px"},
                    maxWidth: "480px",
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: colors.bg,
                        borderBottom: borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <PersonIcon sx={{color: colors.textSecondary, fontSize: "20px"}} />
                            <Typography sx={{
                                fontWeight: 600,
                                fontSize: "16px",
                                color: colors.textPrimary
                            }}>
                                {title || "בחירת מאמן"}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={close}
                            size="small"
                            sx={{
                                color: colors.textMuted,
                                "&:hover": {
                                    backgroundColor: colors.bgHover,
                                    color: colors.textPrimary
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{padding: "24px 20px"}}>
                        {/* Current trainer info */}
                        {trainer && (
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                padding: "12px 16px",
                                backgroundColor: colors.bg,
                                border: borders.default,
                                borderRadius: borders.radius.md,
                                marginBottom: "20px"
                            }}>
                                <Avatar sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: colors.primaryLight,
                                    fontSize: "14px",
                                    color: colors.primary
                                }}>
                                    {trainer.name?.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography sx={{fontSize: "11px", color: colors.textMuted, fontWeight: 500}}>
                                        מאמן נוכחי
                                    </Typography>
                                    <Typography sx={{fontSize: "14px", color: colors.textPrimary, fontWeight: 600}}>
                                        {trainer.name}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Trainer selector */}
                        <Typography sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: colors.textSecondary,
                            marginBottom: "8px"
                        }}>
                            {trainer ? "בחר מאמן חדש" : "בחר מאמן"}
                        </Typography>

                        {trainers && (
                            <Autocomplete
                                value={currentTrainer}
                                disableClearable={false}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                onChange={(event, value) => setCurrentTrainer(value)}
                                options={trainers}
                                noOptionsText="לא נמצאו מאמנים"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: borders.radius.md,
                                        backgroundColor: colors.white,
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: colors.primary
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: colors.primary,
                                            borderWidth: "2px"
                                        }
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: colors.border
                                    }
                                }}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        padding: "8px 12px !important"
                                    }}>
                                        <Avatar sx={{
                                            width: 28,
                                            height: 28,
                                            backgroundColor: colors.primaryLight,
                                            fontSize: "12px",
                                            color: colors.primary
                                        }}>
                                            {option.name?.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography sx={{fontSize: "14px", fontWeight: 500, color: colors.textPrimary}}>
                                                {option.name}
                                            </Typography>
                                            {option.email && (
                                                <Typography sx={{fontSize: "12px", color: colors.textMuted}}>
                                                    {option.email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="חפש מאמן..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <SearchIcon sx={{color: colors.textMuted, fontSize: 20, mr: 0.5}} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                        )}
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        gap: 1.5,
                        padding: "16px 20px",
                        borderTop: borders.default,
                        backgroundColor: colors.bg
                    }}>
                        <Button
                            onClick={() => handleSubmit(currentTrainer?.id)}
                            disabled={!currentTrainer}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.primary,
                                color: colors.white,
                                fontSize: "13px",
                                fontWeight: 600,
                                padding: "8px 24px",
                                borderRadius: borders.radius.md,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: colors.primaryDark
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: colors.border,
                                    color: colors.textMuted
                                }
                            }}
                        >
                            שמור
                        </Button>
                        <Button
                            onClick={close}
                            variant="outlined"
                            sx={{
                                borderColor: colors.border,
                                color: colors.textSecondary,
                                fontSize: "13px",
                                fontWeight: 500,
                                padding: "8px 20px",
                                borderRadius: borders.radius.md,
                                textTransform: "none",
                                "&:hover": {
                                    borderColor: colors.textSecondary,
                                    backgroundColor: colors.bgHover
                                }
                            }}
                        >
                            ביטול
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default ChangeTrainerDialog;
